import { useCallback, useEffect, useRef, useState } from 'react'
import { Document } from '@contentful/rich-text-types'

import Collapsible from '@/components/ui/collapsible'
import RichText from '@/components/ui/rich-text'
import Image from '@/components/ui/image'

import { cx, slugify } from '@/utils/strings'
import { useLocation } from '@/utils/url'
import { smoothScroll } from '@/utils/dom'
import { useInspectorProps } from '@/utils/live-preview'

type AccordionItemProps = {
  entryId?: string
  title: string
  anchorId?: string
  content?: Document | string | null
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
  /** Allow the open state to be managed internally */
  nonControlled?: boolean
  disableHashDetection?: boolean
}

export default function AccordionItem({
  entryId,
  title,
  anchorId,
  content,
  isOpen: _isOpen,
  onToggle,
  nonControlled = false,
  disableHashDetection = false,
}: AccordionItemProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  const ref = useRef<HTMLDivElement>(null)
  const [internalIsOpen, setInternalIsOpen] = useState(!!_isOpen)
  const isOpen = nonControlled ? internalIsOpen : _isOpen
  const id = slugify(anchorId || title)
  const { hash } = useLocation()

  useEffect(() => {
    // Open if the hash matches the ID of this solo accordion item
    if (!disableHashDetection && hash === id) {
      if (nonControlled) setInternalIsOpen(true)
      else onToggle?.(true)
    }
  }, [disableHashDetection, hash])

  // Just render h2 and h3 as normal headings instead of anchor links
  const richH2Renderer = useCallback((node: any, children: any) => <h2>{children}</h2>, [])
  const richH3Renderer = useCallback((node: any, children: any) => <h3>{children}</h3>, [])

  return (
    <div
      id={id}
      ref={ref}
      className="w-full border-t border-black dark:border-white print:break-inside-avoid last:border-b px-3 lg:px-0"
      {...pageInspectorProps({ fieldId: 'title' })}
    >
      <div className="relative group">
        <button
          type="button"
          className={cx(
            'flex gap-6 w-full justify-between items-center font-heading text-left py-7 pr-4 pl-2 text-base'
          )}
          onClick={() => {
            if (nonControlled) setInternalIsOpen(!isOpen)
            onToggle?.(!isOpen)
          }}
        >
          <span className="text-xl lg:text-2xl font-[400]">{title}</span>
          {isOpen ? (
            <span className={cx('bwi bwi-subtract text-2xl')} />
          ) : (
            <span className={cx('bwi bwi-add text-2xl ')} />
          )}
        </button>
        <a
          className={cx(
            'absolute top-0 right-full z-10 opacity-0 group-hover:opacity-40 hover:opacity-100 transition-opacity duration-150',
            'flex items-center justify-center h-full text-2xl p-2'
          )}
          title={`#${id}`}
          href={`#${id}`}
          onClick={(event) => {
            event.preventDefault()
            const { origin, pathname } = window.location
            smoothScroll(event)
            navigator.clipboard.writeText(`${origin}${pathname}#${id}`)
          }}
        >
          <span className="bwi bwi-hashtag" />
        </a>
      </div>
      <Collapsible isOpen={isOpen}>
        <div className={cx(isOpen ? 'pt-3 pb-7' : 'pt-3 pb-3')}>
          {typeof content === 'string' || !content ? (
            content
          ) : (
            <RichText
              body={content}
              h2={richH2Renderer}
              h3={richH3Renderer}
              renderImage={(ref) => (
                <div className="pt-6 pb-12 m-auto w-full [&_svg]:w-full [&_figure]:w-full [&_img]:w-full">
                  <Image image={ref} />
                </div>
              )}
            />
          )}
        </div>
      </Collapsible>
    </div>
  )
}
